import styled, { css } from 'styled-components'

import { Breadcrumbs } from '@Components/Breadcrumbs'
import { ChipsContainer } from '@Components/Chips/ChipsContainer'
import { AccordionHeaderWrapper, AccordionTitle } from '@Components/CMS/Accordion'
import { PersonalisationButtonRoot } from '@Components/Personalization/Button/MakeItPersonalButton'
import { ProductBundleWrapper } from '@Components/Product/Bundle/styled'
import { CrossSells } from '@Components/Product/CrossSells'
import { genericPDPBreakpoint } from '@Components/Product/Display/breakpoint'
import { LinkedProductsRoot } from '@Components/Product/Linked/LinkedProduct.styled'
import { SizeGuideWrapper } from '@Components/Product/SizeGuide/styledElements'
import { TitleAndPriceWrapper } from '@Components/Product/TitleAndPrice'
import { SizeMissing } from '@Components/SizeModal/SizeMissing'
import { SizeOutOfStock } from '@Components/SizeModal/SizeOutOfStock'
import { SizeSelector } from '@Components/SizeSelector'
import { StarRating } from '@Components/StarRating'
import { SmallUrgencyTimer } from '@Components/UrgencyTimer/Small'
import { down, up } from '@Globals/Style/breakpoints'
import { buttonReset, resetButton } from '@Globals/Style/mixins'

export const ProductArticleWrapper = styled.article<{ hasBreadcrumbs?: boolean }>(({
  hasBreadcrumbs,
  theme,
}) => ({
  margin: `${hasBreadcrumbs ? '0' : '10px'} auto 10px`,
  width: '100%',
  maxWidth: `calc(${theme.cmsModule.width.wide} + 60px)`,
  [AccordionTitle]: {
    fontSize: '0.8rem',
  },

  [up(genericPDPBreakpoint)]: {
    marginBottom: 50,

    [AccordionHeaderWrapper]: {
      paddingLeft: 0,
      paddingRight: 5,
    },

    [AccordionTitle]: {
      fontSize: '0.75rem',
      padding: '0.2em 0',
    },
  },

  [up('mobileStyleThreshold')]: {
    padding: '0 30px',
  },

}))

export const PDPTopWrapper = styled.section({
  marginBottom: 10,

  [up(genericPDPBreakpoint)]: {
    alignItems: 'flex-start',
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: 30,
  },
})

export const GalleryWrapper = styled.section<{ isFullScreen?: boolean }>(({
  isFullScreen,
}) => ({
  // I don't like "down", but here it makes sense. If you do default, mobile-first styling, you'll
  // also have to reset the styling to the default rendering as well after the "generic breakpoint"
  // has been reached, which introduces a lot of potential errors if the parent is updated later.
  // Since we're only targeting and overriding a specific breakpoint, this is much simpler and has
  // less chance of going wrong.
  position: 'relative',

  [up(genericPDPBreakpoint)]: {
    flex: '0 0 56%',
    minWidth: 0,
    position: !isFullScreen ? 'sticky' : 'static',
    top: 0,
  },
}))

export const WishListContainer = styled.div`
  display: flex;
  position: absolute;
  top: 2px;
  right: 2px;
  z-index: 1;
`

export const BrandAndReviewWrapper = styled.div(({ theme }) => ({
  alignItems: 'center',
  display: 'flex',
  height: 50,
  justifyContent: 'space-between',
  padding: `0 ${theme.padding.phablet}`,
  pointerEvents: 'none',
  position: 'absolute',
  top: -50,
  width: '100%',
  zIndex: 1,

  '> *': {
    pointerEvents: 'auto',
  },

  [up(genericPDPBreakpoint)]: {
    display: 'block',
    height: 'auto',
    padding: 0,
    position: 'static',
    top: 'auto',
  },
}))

export const VariantAndBuyButtonWrapper = styled.section(({ theme }) => ({
  '> *': {
    flex: '1 0 50%',
    // There's some weird specificity stuff going on in production with this being overridden,
    // so we need to make it important. It doesn't happen in development. ¯\_(ツ)_/¯
    margin: '0 !important',
  },

  // We need the dual specificity here because the buy-button may or may not come with a wrapping DIV.
  // Hitting the button styles by extending nested styled components seems pretty difficult if not
  // outright impossible, so we'll have to rely on a classname here.
  '&, .buy-button': {
    borderRadius: 0,

    [up(genericPDPBreakpoint)]: {
      borderRadius: theme.borderRadius,
    },
  },

  '.buy-button': {
    fontSize: '.875rem',
    minHeight: 46,

    [up('tablet')]: {
      fontSize: 'inherit',
    },
  },

  // We need some styling for the button element if the size selector exists as well.
  '> :not(:first-child)': {
    '&, .buy-button': {
      margin: 0,

      [up(genericPDPBreakpoint)]: {
        borderBottomLeftRadius: 0,
        borderTopLeftRadius: 0,
      },
    },
  },

  alignItems: 'stretch',
  display: 'flex',
  marginBottom: 10,
  minHeight: 46,
  width: '100%',
}))

export const StyledSizeSelector = styled(SizeSelector).withConfig({
  shouldForwardProp: prop => !['lastChild'].includes(prop),
})<{ lastChild?: boolean }>(({
  lastChild,
  theme,
}) => ({
  select: {
    background: theme.colors.greyLightest,
    // Important is set here to override the nested styling inside the wrapper elements used
    border: 'none !important',
    borderRadius: 0,
    fontSize: '.875rem',
    height: '100% !important',

    [up('tablet')]: {
      fontSize: 15,
    },

    [up(genericPDPBreakpoint)]: {
      borderRadius: lastChild ? theme.borderRadius : `${theme.borderRadius} 0 0 ${theme.borderRadius} !important`,
    },

    '&:hover, &:focus': {
      background: theme.colors.greyLighterer,
    },
  },
}))

export const StyledSizeMissing = styled(SizeMissing)(({
  marginBottom: 20,
}))

export const StyledSizeSoldOut = styled(SizeOutOfStock)(({
  marginBottom: 20,
}))

export const StyledUrgencyTimer = styled(SmallUrgencyTimer).withConfig({
  shouldForwardProp: prop => !['hasPersonalization'].includes(prop),
})<{ productHasPersonalization?: boolean }>(({ productHasPersonalization, theme }) => (css`
  ${productHasPersonalization
    ? ''
    : `
      padding-top: ${theme.padding.tablet};
      border-top: 1px solid ${theme.colors.greyLighter};

      ${up(genericPDPBreakpoint)} {
        padding-top: 0;
        margin-top: ${theme.padding.mobile};
        border-top: none;
      }
  `}
`))

// I'd love to use a style object here, but I need the clean-button mixin, so... ¯\_(ツ)_/¯
export const ShippingModalTrigger = styled.button`
  ${resetButton};
  display: block;
  font-size: 0.8rem;
  margin: 5px 0 20px;
  text-align: left;
  text-decoration: underline;
  padding: 0 10px;

  ${up('mobileStyleThreshold')} {
    padding: 0;
  }
`

export const StyledStarRating = styled(StarRating)({
  fontSize: 16,
})

export const ReviewLinkButton = styled.button`${resetButton}`

export const MobileOnlyWrapper = styled.div`
  ${up(genericPDPBreakpoint)} {
    display: none;
  }
`

export const DescriptionHeader = styled.h2(({ theme }) => `
  display: none;

  ${up(genericPDPBreakpoint)} {
    display: block;
    font-size: 1rem;
    font-weight: bold;
    color: ${theme.typography.baseFontColor};
    margin-bottom: ${theme.padding.mobile};
    text-transform: uppercase;
    padding: 0;
  }
`)

export const DescriptionTextWrapper = styled.div(({ theme }) => `
  color: ${theme.colors.grey};

  *:not(h2) {
    color: inherit;
  }

  ${down(genericPDPBreakpoint)} {
    font-size: 0.875rem;
    p {
      margin-bottom: ${theme.padding.phablet};
      padding-bottom: 0;
    }
    div {
      margin-bottom: ${theme.padding.mobile};
    }
  }
`)

export const DescriptionSizeInfo = styled.div(({ theme }) => `
  display: none;

  ${up(genericPDPBreakpoint)} {
    display: block;

    > * {
      margin-top: ${theme.padding.desktop}
    }
  }
`)

export const ProductChips = styled(ChipsContainer)`
  position: absolute;
  top: 10px;
  left: 10px;

  ${up('mobileStyleThreshold')} {
    left: calc(14% + 1px);
  }
`

export const StyledBreadcrumbs = styled(Breadcrumbs)`
  margin: 5px;

  ${up(genericPDPBreakpoint)} {
    margin: 10px 0;
  }
`

export const StyledCrossSells = styled(CrossSells)``

export const PDPInfoWrapper = styled.section`
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;

  ${up(genericPDPBreakpoint)} {
    flex: 0 0 calc(44% - 30px);
    min-width: 0;
    position: static;
  }

  ${up('wide')} {
    flex: 0 0 calc(44% - 50px);
  }

,
> * {
  // This will ensure that all "un-ordered" items will just show up where they're rendered.
  order: 99
}

  // We need to order stuff in a special way on mobile. So let's do that.
  ${BrandAndReviewWrapper} {
    order: 0
  }

  ${TitleAndPriceWrapper} {
    order: 1
  }

  ${ProductBundleWrapper} {
    order: 2
  }

  ${VariantAndBuyButtonWrapper} {
    order: 3
  }

  ${SizeGuideWrapper} {
    order: 4
  }

  ${PersonalisationButtonRoot} {
    order: 5
  }

  ${StyledUrgencyTimer} {
    order: 6
  }

  ${ShippingModalTrigger} {
    order: 7
  }

  ${StyledSizeMissing} {
    order: 8
  }

  ${StyledSizeSoldOut} {
    order: 9
  }

  ${StyledCrossSells} {
    order: 10
  }

  ${LinkedProductsRoot} {
    order: 11
  }

  ${up(genericPDPBreakpoint)} {
    ${BrandAndReviewWrapper},
    ${TitleAndPriceWrapper},
    ${ProductBundleWrapper},
    ${VariantAndBuyButtonWrapper},
    ${PersonalisationButtonRoot},
    ${SizeGuideWrapper},
    ${StyledUrgencyTimer},
    ${ShippingModalTrigger},
    ${StyledSizeMissing},
    ${StyledSizeSoldOut},
    ${StyledCrossSells},
    ${LinkedProductsRoot} {
      order: 99
    }
  }
`

export const WishlistWrapper = styled.div`
  z-index: 1;
  position: absolute;
  right: 2px;
  top: 2px;
`

export const StarRatingWrapper = styled.div(() => css`
  right: 0;
  position: absolute;
  margin: 10px;
`)

export const Image = styled.img(() => css`
  display: inline-block;
  height: 13px;
  width: auto;
  max-width: 100%;
  opacity: 0.75;
  vertical-align: top;
`)

export const Anchor = styled.a(() => css`
  height: 13px;
  line-height: 13px;
  max-width: 200px;
`)

export const BrandSection = styled.div(({ theme }) => css`
  position: relative;
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  background-color: rgba(0, 0, 0, 0.04);
  height: 50px;
  padding: 0 10px;
  -webkit-box-align: center;
  align-items: center;
  -webkit-box-pack: justify;
  @media (min-width: ${theme.breakpoints.mobileStyleThreshold}) {
    display: none;
  },
`)

export const ZoomWrapper = styled.div`
  z-index: 1;
  position: absolute;
  bottom: 50px;
  right: 0;
  padding: 8px 7px;

  ${up('mobileStyleThreshold')} {
    bottom: 10px;
  }
`

export const ZoominButton = styled.button(({ theme }) => css`
  ${buttonReset};
  position: absolute;
  right: 5px;
  bottom: 0;
  background: transparent !important;
  padding: 10px;

  > svg {
    fill: ${theme.colors.grey};
    width: 24px;
    height: 24px;
    vertical-align: bottom;
  }

  ${up('mobileStyleThreshold')} {
    padding: 7px;
    bottom: 5px;
  }
`)
export const LayoutV2 = styled.section(() => css`
  display: grid;
  grid-template-columns: 1fr;
  gap: 10px;
  grid-gap: 10px;
  align-items: start;

  ${up('mobileStyleThreshold')} {
    gap: 30px;
    grid-gap: 30px;
    grid-template-columns: 56% 1fr;
  }

  ${up('wide')} {
    gap: 50px;
    grid-gap: 50px;
  }
`)
export const InfoWrapperV2 = styled.section(() => css`
  overflow: hidden;

  > div:first-child {
    display: none;
  }

  ${up('mobileStyleThreshold')} {
    > div:first-child {
      display: flex;
    }
  }
`)
export const ProductChipsWrapper = styled(ChipsContainer)`
  top: 10px;
  left: 10px;
  font-size: 0.665rem;

  ${up('mobileStyleThreshold')} {
    left: 98px;
  }
`

export const FullscreenSliderDotWrapper = styled.div(({ theme }) => css`
  position: fixed;
  bottom: ${theme.padding.phablet};
  display: flex;
  justify-content: center;
  align-items: center;
  left: 0;
  right: 0;
  z-index: 9001;
  margin: 0 auto;
  width: min-content;
`)
